import { format } from "date-fns";
import type { TFunction } from "i18next";
import { useState } from "react";
import type { useU2000FormStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { emptyEmployeeBillingAssignment, type U3121EmployeeBillingAssignment } from "../../../../U3121Service";
import { U2000FormStateEnumEditMode, U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { generateTemporaryId } from "../../../../../u2000Core/tools/U2000IdUtils";
import type { U3121ProjectFormExtendedHandlers } from "../u3121ProjectContactsTab/U3121ProjectContactHandlers";

const NS = "u3121";
const NO_END = "9999-12-31";

/** Retourne true si les deux périodes [from, to] se chevauchent (to null = pas de fin). */
function periodsOverlap(from1: string | undefined | null, to1: string | undefined | null, from2: string | undefined | null, to2: string | undefined | null): boolean {
  const a1 = (from1 ?? "").trim() || "0000-01-01";
  const b1 = (to1 ?? "").trim() || NO_END;
  const a2 = (from2 ?? "").trim() || "0000-01-01";
  const b2 = (to2 ?? "").trim() || NO_END;
  return a1 <= b2 && a2 <= b1;
}

/** Vérifie doublon : même employé + même tâche avec période qui se chevauche. */
function getOverlapErrors(
  formData: U3121EmployeeBillingAssignment | null | undefined,
  existingRows: U3121EmployeeBillingAssignment[],
  excludeRowId: string | undefined,
  t: TFunction,
  ns: string,
): Record<string, string> {
  const result: Record<string, string> = {};
  if (!formData?.validFrom) return result;
  const formEmpId = formData.employee?.id ?? "";
  const formTaskId = formData.task?.id ?? "";
  const formFrom = formData.validFrom;
  const formTo = formData.validTo ?? null;

  // Même employé et même tâche (vide = vide) avec période qui se chevauche = doublon
  const hasOverlap = existingRows.some((row) => {
    if (excludeRowId && row.id === excludeRowId) return false;
    const rowEmpId = row.employee?.id ?? "";
    const rowTaskId = row.task?.id ?? "";
    const sameEmployee = formEmpId === rowEmpId;
    const sameTask = formTaskId === rowTaskId;
    if (!sameEmployee || !sameTask) return false;
    return periodsOverlap(formFrom, formTo, row.validFrom, row.validTo);
  });

  if (hasOverlap) {
    result["employee.id"] = t("errDuplicateAssignmentOverlap", { ns });
    result["task.id"] = t("errDuplicateAssignmentOverlap", { ns });
  }
  return result;
}

export interface U3121ProjectEmployeeAssignmentHandlerParam {
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3121EmployeeBillingAssignment>>;
  t: TFunction;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U3121EmployeeBillingAssignment>>;
  parentFormHandlers: U3121ProjectFormExtendedHandlers;
}

export interface U3121ProjectEmployeeAssignmentHandlerReturn {
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  onRowClick: (row: U3121EmployeeBillingAssignment) => void;
  onDlgClose: (result: string) => void;
  isDialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isDeleteDialogOpen: boolean;
  onDlgDeleteClose: (result: string) => void;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U3121EmployeeBillingAssignment>>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3121EmployeeBillingAssignment>>;
  dataChangedEvent: number;
}

export function u3121ProjectEmployeeAssignmentHandler(params: U3121ProjectEmployeeAssignmentHandlerParam): U3121ProjectEmployeeAssignmentHandlerReturn {
  const { gridStateHandlers, formStateHandlers, parentFormHandlers } = params;
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [dataChangedEvent, setDataChangedEvent] = useState<number>(0);

  const onAdd = (): void => {
    // setEditMode(ADD) doit être appelé avant setData : il réinitialise avec newRecord(), qu’on écrase ensuite avec la date par défaut
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    const defaultValidFrom = format(new Date(), "yyyy-MM-dd");
    formStateHandlers.setData({ ...emptyEmployeeBillingAssignment, validFrom: defaultValidFrom });
    setDialogOpen(true);
  };

  const onModify = (): void => {
    const selectedRow = gridStateHandlers.getSelectedRows()[0];
    formStateHandlers.setData(selectedRow as U3121EmployeeBillingAssignment);
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    setDialogOpen(true);
  };

  const onDelete = (): void => {
    setDeleteDialogOpen(true);
  };

  const onRowClick = (row: U3121EmployeeBillingAssignment): void => {
    formStateHandlers.setData(row);
    const parentFormState = parentFormHandlers.getFormState?.();
    if (parentFormState === U2000FormStateEnumState.EDIT) {
      formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    } else {
      formStateHandlers.setEditMode(U2000FormStateEnumEditMode.NONE);
    }
    setDialogOpen(true);
  };

  const onDlgClose = (result: string): void => {
    if (result === "cancel") {
      gridStateHandlers.setErrors([]);
      formStateHandlers.cancelData();
      setDialogOpen(false);
    } else if (result === "save") {
      // Valider les données avant de sauvegarder (même ordre que U2101)
      const businessHandlers = formStateHandlers.getBusinessHandlers();
      if (businessHandlers && businessHandlers.validateForm) {
        const errors = businessHandlers.validateForm(formStateHandlers.getData());
        formStateHandlers.setErrors(errors);
        formStateHandlers.saveData();
        if (Object.keys(errors).length > 0) {
          return;
        }
      }

      const formData = formStateHandlers.getData();
      const isEdit = formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT;
      const excludeRowId = isEdit ? (gridStateHandlers.getSelectedRows()[0]?.id as string | undefined) : undefined;
      const overlapErrors = getOverlapErrors(formData, gridStateHandlers.getData(), excludeRowId, params.t, NS);
      if (Object.keys(overlapErrors).length > 0) {
        // Ne pas appeler saveData() : il rejoue validateForm et écrase les erreurs d’overlap.
        formStateHandlers.setErrors(overlapErrors);
        formStateHandlers.setSubmitted(true);
        return;
      }

      // Normaliser les données : sauvegarder employee, task, hourRate, validFrom, validTo
      const employee = formData?.employee as unknown as Record<string, unknown>;
      const task = formData?.task as unknown as Record<string, unknown>;
      const hourRate = formData?.hourRate as number;
      const validFrom = formData?.validFrom as string | undefined;
      const validTo = formData?.validTo as string | undefined;

      const employeeId = (employee?.id as string) || "";
      const taskId = (task?.id as string) || "";
      const hasEmployeeOrTask = Boolean(employeeId.trim() || taskId.trim());
      // Id ligne : combinaison des deux quand présents, sinon temporaire (validation exige au moins un des deux)
      const lineId = [employeeId, taskId].filter(Boolean).join("_") || generateTemporaryId();

      const normalizedData: U3121EmployeeBillingAssignment = {
        id: lineId,
        employee: employee?.id
          ? {
              id: employee.id as string,
              firstName: (employee.firstName as string) || undefined,
              lastName: (employee.lastName as string) || undefined,
              codeEmployee: (employee.codeEmployee as string) || undefined,
            }
          : undefined,
        task: task?.id
          ? {
              id: task.id as string,
              taskCode: (task.taskCode as string) || undefined,
              taskDescription: (task.taskDescription as string) || undefined,
              taskDescriptionFr: (task.taskDescriptionFr as string) || undefined,
              taskDescriptionEn: (task.taskDescriptionEn as string) || undefined,
            }
          : undefined,
        hourRate: hasEmployeeOrTask && hourRate !== undefined && hourRate !== null ? hourRate : undefined,
        validFrom: validFrom != null && String(validFrom).trim() !== "" ? String(validFrom).trim() : undefined,
        validTo: validTo != null && String(validTo).trim() !== "" ? String(validTo).trim() : undefined,
      };

      if (formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT) {
        // Ligne existante : remplacer par l'id de la ligne qu'on édite (selectedRow), préserver source
        const originalRow = gridStateHandlers.getSelectedRows()[0];
        const originalId = originalRow?.id as string | undefined;
        const idToReplace = originalId ?? normalizedData.id;
        const withSource = { ...normalizedData, source: originalRow?.source ?? "project", sourceLabelFr: originalRow?.sourceLabelFr, sourceLabelEn: originalRow?.sourceLabelEn };
        const updatedData = gridStateHandlers.getData().map((item) => (item.id === idToReplace ? withSource : item));
        gridStateHandlers.setData(updatedData);
        setDataChangedEvent(dataChangedEvent + 1);
      } else if (formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.ADD) {
        // Nouvelle ligne : ajouter avec un id déterministe ou temporaire et source "project"
        const newData = { ...normalizedData, id: normalizedData.id || generateTemporaryId(), source: "project" as const };
        gridStateHandlers.setData([...gridStateHandlers.getData(), newData]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        setDataChangedEvent(dataChangedEvent + 1);
      }
      formStateHandlers.afterSaveData(formStateHandlers.getData());
    }
    setDialogOpen(false);
    return;
  };

  const onDlgDeleteClose = (result: string): void => {
    if (result === "yes") {
      const selectedRows = gridStateHandlers.getSelectedRows();
      const remainingRows = gridStateHandlers.getData().filter((row) => !selectedRows.some((selected) => selected.id === row.id));
      gridStateHandlers.setData(remainingRows);
      gridStateHandlers.setTotalRows(remainingRows.length);
      setDataChangedEvent(dataChangedEvent + 1);
    }
    setDeleteDialogOpen(false);
    return;
  };

  return {
    onAdd,
    onModify,
    onDelete,
    onRowClick,
    onDlgClose,
    isDialogOpen,
    setDialogOpen,
    isDeleteDialogOpen,
    onDlgDeleteClose,
    formStateHandlers,
    gridStateHandlers,
    dataChangedEvent,
  };
}
